<template>
  <transition name="interstitial-fade">
    <div v-if="isVisible" class="interstitial-overlay" @click.self="handleMaybeLater">
      <!-- Animated background -->
      <div class="interstitial-bg">
        <div class="bg-gradient"></div>
        <div class="bg-pattern"></div>
        <div class="floating-circles">
          <span v-for="n in 8" :key="n" class="circle" :style="getCircleStyle(n)"></span>
        </div>
      </div>

      <!-- Content card -->
      <div class="interstitial-card">
        <!-- Top decorative line -->
        <div class="card-top-glow"></div>

        <!-- Crown icon -->
        <div class="crown-container">
          <div class="crown-bg">
            <i class="fas fa-crown crown-icon"></i>
          </div>
          <div class="crown-rings">
            <span class="ring ring-1"></span>
            <span class="ring ring-2"></span>
            <span class="ring ring-3"></span>
          </div>
        </div>


        <!-- Main heading -->
        <h2 class="interstitial-title">
          You're enjoying <span class="gold-text">No1 OTT!</span>
        </h2>
        <p class="interstitial-subtitle">
          Unlock <strong>500+</strong> premium videos, ad-free streaming, and HD quality
        </p>

        <!-- Feature list -->
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <i class="fas fa-film"></i>
            </div>
            <span>500+ Premium Videos</span>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <i class="fas fa-ban"></i>
            </div>
            <span>Ad-Free Experience</span>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <i class="fas fa-hd"></i>
            </div>
            <span>HD & 4K Quality</span>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <i class="fas fa-mobile-alt"></i>
            </div>
            <span>Watch Anywhere</span>
          </div>
        </div>

        <!-- Pricing highlight -->
        <div class="price-highlight">
          <span class="price-label">Starting at just</span>
          <span class="price-amount">₹1<span class="price-period">/day</span></span>
        </div>

        <!-- CTA buttons -->
        <div class="interstitial-actions">
          <router-link to="/plans" class="cta-subscribe" @click="handleSubscribeClick">
            <i class="fas fa-crown cta-icon"></i>
            <span>Subscribe Now</span>
            <span class="cta-shine"></span>
          </router-link>
          <button class="cta-later" @click="handleMaybeLater">
            Maybe Later
          </button>
        </div>

        <!-- Social proof -->
        <div class="social-proof">
          <div class="avatars">
            <span class="avatar" v-for="n in 4" :key="n" :style="getAvatarStyle(n)">
              <i class="fas fa-user"></i>
            </span>
          </div>
          <span class="proof-text">Join <strong>10,000+</strong> happy subscribers</span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'SubscribeInterstitial',
  props: {

  },
  data() {
    return {
      isVisible: false,
      freeVideosWatched: 0,
      pageNavigations: 0,
      triggerThreshold: 5,          // Show after 5 page navigations
      cooldownPeriod: 1800000,      // 30 minutes cooldown after dismiss
      sessionKey: 'interstitial_session_count',
      dismissKey: 'interstitial_dismissed_at',
      watchCountKey: 'free_videos_watched'
    }
  },
  computed: {
    isUserSubscribed() {
      // Check both sources for reliable subscription status
      if (this.$store.getters.hasActiveSubscription) return true
      const user = this.$store.getters.currentUser
      if (!user) return false
      return user.is_subscribed && new Date(user.subscription_end_date) > new Date()
    }
  },
  created() {
    // Load watch count
    this.freeVideosWatched = parseInt(localStorage.getItem(this.watchCountKey) || '0')
  },
  mounted() {
    if (this.isUserSubscribed) return

    // Listen for route changes
    this.$watch(
      () => this.$route.path,
      () => {
        this.onPageNavigate()
      }
    )
    
    // Watch for subscription status changes
    this.$watch(
      () => this.isUserSubscribed,
      (isSubscribed) => {
        if (isSubscribed && this.isVisible) {
          this.isVisible = false
          document.body.style.overflow = ''
        }
      }
    )

    // Initialize session count
    const sessionCount = parseInt(sessionStorage.getItem(this.sessionKey) || '0')
    this.pageNavigations = sessionCount

    // Count initial page load as a navigation
    this.onPageNavigate()

    // Timer-based fallback: show after 60 seconds on site even without navigating
    this.timerTrigger = setTimeout(() => {
      if (!this.isVisible && !this.isUserSubscribed) {
        // Check cooldown
        const dismissedAt = localStorage.getItem(this.dismissKey)
        if (dismissedAt) {
          const timeSince = Date.now() - parseInt(dismissedAt)
          if (timeSince < this.cooldownPeriod) return
        }
        this.showInterstitial()
      }
    }, 60000)
  },
  beforeUnmount() {
    if (this.timerTrigger) clearTimeout(this.timerTrigger)
  },
  methods: {
    onPageNavigate() {
      if (this.isUserSubscribed || this.isVisible) return

      this.pageNavigations++
      sessionStorage.setItem(this.sessionKey, this.pageNavigations.toString())

      // Check cooldown
      const dismissedAt = localStorage.getItem(this.dismissKey)
      if (dismissedAt) {
        const timeSince = Date.now() - parseInt(dismissedAt)
        if (timeSince < this.cooldownPeriod) return
      }

      // Trigger at threshold multiples (5, 10, 15...)
      if (this.pageNavigations > 0 && this.pageNavigations % this.triggerThreshold === 0) {
        // Small delay so the page loads first
        setTimeout(() => {
          this.showInterstitial()
        }, 1500)
      }
    },
    showInterstitial() {
      if (this.isUserSubscribed) return
      this.freeVideosWatched = parseInt(localStorage.getItem(this.watchCountKey) || '0')
      if (this.freeVideosWatched < 1) {
        this.freeVideosWatched = this.pageNavigations
      }
      this.isVisible = true
      // Prevent background scroll
      document.body.style.overflow = 'hidden'
    },
    handleSubscribeClick() {
      this.isVisible = false
      document.body.style.overflow = ''
      localStorage.setItem('interstitial_converted', Date.now().toString())
    },
    handleMaybeLater() {
      this.isVisible = false
      document.body.style.overflow = ''
      localStorage.setItem(this.dismissKey, Date.now().toString())
      // Increase threshold for next show
      this.triggerThreshold = 10
    },
    incrementWatchCount() {
      this.freeVideosWatched++
      localStorage.setItem(this.watchCountKey, this.freeVideosWatched.toString())
    },
    getCircleStyle(n) {
      const sizes = [120, 80, 150, 60, 100, 90, 70, 130]
      const positions = [
        { top: '10%', left: '5%' },
        { top: '20%', right: '10%' },
        { bottom: '15%', left: '15%' },
        { top: '50%', right: '5%' },
        { bottom: '30%', right: '20%' },
        { top: '5%', left: '40%' },
        { bottom: '5%', left: '50%' },
        { top: '40%', left: '10%' }
      ]
      const delays = [0, 1.5, 3, 0.5, 2, 4, 1, 2.5]
      return {
        width: sizes[n - 1] + 'px',
        height: sizes[n - 1] + 'px',
        ...positions[n - 1],
        animationDelay: delays[n - 1] + 's'
      }
    },
    getAvatarStyle(n) {
      const colors = ['#c30059', '#ff4d4d', '#FFD700', '#8b5cf6']
      return {
        background: colors[n - 1],
        zIndex: 5 - n,
        marginLeft: n > 1 ? '-8px' : '0'
      }
    }
  }
}
</script>

<style scoped>
.interstitial-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

/* Animated background */
.interstitial-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.bg-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, 
    rgba(10, 0, 20, 0.97) 0%, 
    rgba(30, 0, 50, 0.97) 30%,
    rgba(80, 0, 50, 0.97) 60%,
    rgba(10, 0, 20, 0.97) 100%
  );
}

.bg-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(195, 0, 89, 0.1) 1px, transparent 1px);
  background-size: 30px 30px;
  animation: patternDrift 20s linear infinite;
}

@keyframes patternDrift {
  0% { transform: translate(0, 0); }
  100% { transform: translate(30px, 30px); }
}

.floating-circles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.circle {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(195, 0, 89, 0.2);
  animation: circleFloat 8s infinite ease-in-out;
}

@keyframes circleFloat {
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
  50% { transform: translate(20px, -20px) scale(1.1); opacity: 0.6; }
}

/* Content card */
.interstitial-card {
  position: relative;
  width: 100%;
  max-width: 420px;
  background: linear-gradient(165deg, 
    rgba(30, 15, 35, 0.95) 0%, 
    rgba(20, 10, 30, 0.98) 100%
  );
  border-radius: 24px;
  padding: 2rem 1.75rem;
  text-align: center;
  border: 1px solid rgba(195, 0, 89, 0.25);
  box-shadow: 
    0 25px 60px rgba(0, 0, 0, 0.5),
    0 0 100px rgba(195, 0, 89, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  animation: cardEntry 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-top-glow {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #c30059, #FFD700, #c30059, transparent);
  border-radius: 2px;
}

@keyframes cardEntry {
  from {
    transform: scale(0.9) translateY(30px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

/* Crown */
.crown-container {
  position: relative;
  width: 72px;
  height: 72px;
  margin: 0 auto 1.25rem;
}

.crown-bg {
  position: relative;
  z-index: 2;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 25px rgba(255, 215, 0, 0.4);
}

.crown-icon {
  font-size: 1.8rem;
  color: #1a0a00;
  animation: crownFloat 3s infinite ease-in-out;
}

@keyframes crownFloat {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-3px) rotate(5deg); }
}

.crown-rings {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.ring {
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  border: 1px solid rgba(255, 215, 0, 0.3);
  animation: ringExpand 3s infinite ease-out;
}

.ring-2 { animation-delay: 1s; }
.ring-3 { animation-delay: 2s; }

@keyframes ringExpand {
  0% { transform: scale(1); opacity: 0.5; }
  100% { transform: scale(1.8); opacity: 0; }
}

/* Stats badge */
.usage-stats {
  margin-bottom: 1rem;
}

.stat-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: rgba(195, 0, 89, 0.15);
  border: 1px solid rgba(195, 0, 89, 0.3);
  border-radius: 50px;
  color: #ff6b9d;
  font-size: 0.75rem;
  font-weight: 500;
}

.stat-badge i {
  font-size: 0.8rem;
}

/* Title */
.interstitial-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
  margin: 0 0 0.5rem;
  line-height: 1.3;
}

.gold-text {
  background: linear-gradient(135deg, #FFD700, #FFA500);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.interstitial-subtitle {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.85rem;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.interstitial-subtitle strong {
  color: white;
}

/* Features grid */
.features-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 1.25rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.7rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(195, 0, 89, 0.1);
  border-color: rgba(195, 0, 89, 0.2);
}

.feature-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(195, 0, 89, 0.2), rgba(195, 0, 89, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ff6b9d;
  font-size: 0.7rem;
}

/* Price highlight */
.price-highlight {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  margin-bottom: 1.25rem;
  padding: 10px;
  background: rgba(255, 215, 0, 0.05);
  border: 1px dashed rgba(255, 215, 0, 0.2);
  border-radius: 12px;
}

.price-label {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
}

.price-amount {
  font-size: 1.5rem;
  font-weight: 800;
  color: #FFD700;
}

.price-period {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(255, 215, 0, 0.7);
}

/* CTA buttons */
.interstitial-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 1.25rem;
}

.cta-subscribe {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #c30059, #ff0077);
  color: white;
  border: none;
  border-radius: 14px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 6px 20px rgba(195, 0, 89, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.cta-subscribe:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 10px 30px rgba(195, 0, 89, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.cta-icon {
  color: #FFD700;
  font-size: 0.9rem;
}

.cta-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
  animation: shine 4s infinite;
}

@keyframes shine {
  0% { left: -100%; }
  50% { left: 200%; }
  100% { left: 200%; }
}

.cta-later {
  padding: 10px 28px;
  background: none;
  color: rgba(255, 255, 255, 0.4);
  border: none;
  border-radius: 10px;
  font-weight: 500;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cta-later:hover {
  color: rgba(255, 255, 255, 0.7);
}

/* Social proof */
.social-proof {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.avatars {
  display: flex;
  align-items: center;
}

.avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.55rem;
  color: white;
  border: 2px solid rgba(20, 10, 30, 0.95);
}

.proof-text {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.7rem;
}

.proof-text strong {
  color: rgba(255, 255, 255, 0.6);
}

/* Transitions */
.interstitial-fade-enter-active {
  transition: opacity 0.4s ease;
}

.interstitial-fade-enter-active .interstitial-card {
  animation: cardEntry 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.interstitial-fade-leave-active {
  transition: opacity 0.3s ease;
}

.interstitial-fade-enter-from,
.interstitial-fade-leave-to {
  opacity: 0;
}

/* Mobile adjustments */
@media (max-width: 480px) {
  .interstitial-card {
    padding: 1.5rem 1.25rem;
    border-radius: 20px;
  }

  .interstitial-title {
    font-size: 1.25rem;
  }

  .features-grid {
    gap: 8px;
  }

  .feature-item {
    padding: 6px 8px;
    font-size: 0.65rem;
  }

  .feature-icon {
    width: 24px;
    height: 24px;
    font-size: 0.6rem;
  }

  .cta-subscribe {
    padding: 12px 24px;
    font-size: 0.9rem;
  }

  .price-amount {
    font-size: 1.3rem;
  }

  .crown-container {
    width: 60px;
    height: 60px;
  }

  .crown-bg {
    width: 60px;
    height: 60px;
  }

  .crown-icon {
    font-size: 1.5rem;
  }
}
</style>
